import React, { useState, useEffect } from 'react';

/* ── Store floor plan, top-down, in SVG coordinates ──
   Mirrors the 3D scene: 40×30 floor, 4 aisles (x = -12,-4,4,12),
   entrance at bottom-left, checkouts at right.                 */

// scale 3D coords (−20..20, −15..15) → SVG (10..210, 15..115)
const MX = (x) => 10 + (x + 20) * 5;
const MZ = (z) => 15 + (z + 15) * (100 / 30);

const AISLE_X = [-12, -4, 4, 12];
const SHELF_HALF_W = 0.45; // 3D units

// shelf rectangles (both faces of each aisle)
const SHELVES = AISLE_X.flatMap((ax) =>
  [-1.4, 1.4].map((off) => ({
    x: MX(ax + off - SHELF_HALF_W),
    y: MZ(-11),
    w: SHELF_HALF_W * 2 * 5,
    h: (MZ(7) - MZ(-11)),
  }))
);

// shoppers — same cohort positions as the 3D scene, animated along paths
const PATHS = [
  { color: '#f59e0b', path: [[-5, 13.5], [-12, 8.5], [-12, -9], [-12, -13], [-4, -13], [-4, 2], [-4, 8.5], [4, 8.5], [4, -9], [4, -13], [12, -13], [12, 3], [12, 8.5], [10, 12], [7, 12.5], [-5, 13.5]] },
  { color: '#60a5fa', path: [[-5, 13.5], [-4, 8.5], [-4, -8], [-4, 8.5], [4, 8.5], [4, -6], [4, 8.5], [12, 8.5], [12, -4], [12, 8.5], [9, 12], [-5, 13.5]] },
  { color: '#10b981', path: [[-5, 13.5], [-17, 8.5], [-17, -13.5], [-14, -12], [-9, -13.5], [4, -13.5], [4, 8.5], [10, 12], [-5, 13.5]] },
  { color: '#a78bfa', path: [[-5, 13.5], [9, 12], [16.5, 8], [16.5, -11], [16.5, 8], [9, 12], [-5, 13.5]] },
  { color: '#ef4444', path: [[-5, 13.5], [-4, 8.5], [4, 8.5], [4, 2], [4.5, -5], [4, 8.5], [-5, 13.5]] },
];

// deterministic point cloud scattered over the floor plan
function mulberry32(a) {
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
const rand = mulberry32(42);
const CLOUD = [
  // dense points along the shelves (structure)
  ...SHELVES.flatMap((s, i) => {
    const pts = [];
    for (let k = 0; k < 40; k++) {
      pts.push([s.x + rand() * s.w, s.y + rand() * s.h, 1.3]);
    }
    return pts;
  }),
  // wall points
  ...Array.from({ length: 60 }, () => [10 + rand() * 200, 15 + (rand() < 0.5 ? rand() * 4 : 96 + rand() * 4), 1.1]),
  // sparse floor points (noise)
  ...Array.from({ length: 45 }, () => [12 + rand() * 196, 20 + rand() * 90, 0.5]),
];

// warehouse records anchored to shelves (fused mode) — SKU batches
const RECORDS = [
  { x: MX(-12.5), z: MZ(-6), label: 'SKU 4412 · 820 u' },
  { x: MX(-3.5),  z: MZ(-2), label: 'SKU 1087 · 1.4k u' },
  { x: MX(3.5),   z: MZ(3),  label: 'SKU 7731 · 96 u ▲' },
  { x: MX(11.5),  z: MZ(-8), label: 'SKU 2210 · 410 u' },
];

// a shopper position along its path, parameter t in [0,1)
function pointOnPath(path, t) {
  const segs = path.length - 1;
  const f = t * segs;
  const i = Math.min(Math.floor(f), segs - 1);
  const u = f - i;
  const [x1, z1] = path[i];
  const [x2, z2] = path[i + 1];
  return [x1 + (x2 - x1) * u, z1 + (z2 - z1) * u];
}

const MODES = ['point', 'mesh', 'fused'];

export function FusionLayer() {
  const [mode, setMode] = useState('point');
  const [t, setT] = useState(0);

  // animate shoppers in fused mode
  useEffect(() => {
    if (mode !== 'fused') return;
    const id = setInterval(() => setT((v) => (v + 0.002) % 1), 40);
    return () => clearInterval(id);
  }, [mode]);

  const op = (m) => (mode === m ? 1 : 0);

  return (
    <section id="fusion" className="scroll-mt-24">
      <div className="flex items-center gap-4 mb-5">
        <span className="font-mono text-sm text-white border border-white/20 rounded-lg px-2.5 py-1.5 bg-white/5 shrink-0">03</span>
        <div>
          <h3 className="text-base font-bold text-white">Fusion</h3>
          <p className="text-sm text-white/40">The store above, pre-processed and bound to live truth</p>
        </div>
      </div>

      <div className="grid md:grid-cols-[1.5fr_1fr] gap-3">
        {/* ── viz: the same store floor plan, three stages ── */}
        <div className="border border-white/10 rounded-xl bg-black/50 p-3">
          <svg viewBox="0 0 220 120" className="block w-full">
            {/* floor outline (always faintly present) */}
            <rect x="10" y="15" width="200" height="100" fill="none"
                  stroke="rgba(255,255,255,.12)" strokeWidth="0.6" />

            {/* STAGE 1: raw point cloud of the store */}
            <g className="transition-opacity duration-700" style={{ opacity: op('point') }}>
              {CLOUD.map(([x, y, r], i) => (
                <circle key={i} cx={x} cy={y} r={r} fill="rgba(255,255,255,.7)" />
              ))}
            </g>

            {/* STAGE 2: reconstructed mesh — shelves, walls, entrance */}
            <g className="transition-opacity duration-700" style={{ opacity: op('mesh') }}>
              {/* point ghost */}
              <g opacity="0.15">
                {CLOUD.map(([x, y, r], i) => (
                  <circle key={i} cx={x} cy={y} r={r} fill="rgba(255,255,255,.7)" />
                ))}
              </g>
              {/* shelves as wireframe */}
              {SHELVES.map((s, i) => (
                <rect key={i} x={s.x} y={s.y} width={s.w} height={s.h}
                      fill="rgba(255,255,255,.06)" stroke="rgba(255,255,255,.55)" strokeWidth="0.8" />
              ))}
              {/* aisle centre-lines */}
              {AISLE_X.map((ax) => (
                <line key={ax} x1={MX(ax)} y1={MZ(-11)} x2={MX(ax)} y2={MZ(7)}
                      stroke="rgba(255,255,255,.18)" strokeWidth="0.5" strokeDasharray="2 2" />
              ))}
              {/* entrance */}
              <rect x={MX(-6.5)} y="112" width="15" height="2.5" fill="#10b981" opacity="0.8" />
            </g>

            {/* STAGE 3: fused — mesh + shoppers + anchored records */}
            <g className="transition-opacity duration-700" style={{ opacity: op('fused') }}>
              {SHELVES.map((s, i) => (
                <rect key={i} x={s.x} y={s.y} width={s.w} height={s.h}
                      fill="rgba(255,255,255,.06)" stroke="rgba(255,255,255,.35)" strokeWidth="0.6" />
              ))}
              {/* shoppers, live */}
              {PATHS.map((p, i) => {
                const [x, z] = pointOnPath(p.path, (t + i * 0.23) % 1);
                return (
                  <circle key={i} cx={MX(x)} cy={MZ(z)} r="1.8" fill={p.color}>
                    <title>{`${p.color} cohort`}</title>
                  </circle>
                );
              })}
              {/* red anomaly shopper, pulsing halo */}
              {(() => {
                const [x, z] = pointOnPath(PATHS[4].path, t % 1);
                return (
                  <g>
                    <circle cx={MX(x)} cy={MZ(z)} r="4.5" fill="none"
                            stroke="#ef4444" strokeWidth="0.6"
                            className="animate-[fusepulse_1.6s_ease-in-out_infinite]" />
                  </g>
                );
              })()}
              {/* anchored warehouse records */}
              {RECORDS.map((r) => (
                <g key={r.label}>
                  <circle cx={r.x} cy={r.z} r="2.4" fill="none" stroke="#10b981" strokeWidth="0.9" />
                  <circle cx={r.x} cy={r.z} r="0.9" fill="#10b981" />
                  <text x={r.x + 4.5} y={r.z + 1.5}
                        fill="rgba(255,255,255,.75)" fontSize="4.2" fontFamily="monospace">
                    {r.label}
                  </text>
                </g>
              ))}
              {/* entrance */}
              <rect x={MX(-6.5)} y="112" width="15" height="2.5" fill="#10b981" opacity="0.8" />
            </g>
          </svg>

          <div className="mt-2 flex gap-1.5">
            {MODES.map((m) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                className={`flex-1 font-mono text-[10px] tracking-widest uppercase rounded-md py-1.5 border transition-colors ${
                  mode === m
                    ? 'bg-white text-black border-white font-bold'
                    : 'text-white/50 border-white/10 hover:text-white'
                }`}
              >
                {m === 'point' ? 'Point cloud' : m === 'mesh' ? 'Mesh' : 'Fused'}
              </button>
            ))}
          </div>
          <p className="mt-2 font-mono text-[9px] tracking-widest uppercase text-white/30 text-center">
            {mode === 'point' && 'Raw scan of the same floor plan · 265 points'}
            {mode === 'mesh' && 'Reconstructed geometry · 8 shelves, 4 aisles, entrance'}
            {mode === 'fused' && 'Geometry + shoppers + warehouse records anchored to shelves'}
          </p>
        </div>

        {/* ── metrics ── */}
        <div className="flex flex-col gap-3">
          <div className="border border-white/10 rounded-xl bg-black/50 p-3 flex-1 flex flex-col justify-center">
            <small className="font-mono text-[10px] tracking-widest uppercase text-white/30">Bound records</small>
            <b className="text-white font-bold">14.2M <span className="text-emerald-400">● live</span></b>
            <div className="flex items-end gap-[3px] h-6 mt-2" aria-hidden="true">
              {[60, 80, 45, 90, 70, 55].map((h, i) => (
                <i key={i} className="flex-1 bg-white/20 rounded-t-sm origin-bottom animate-[minibar_2.8s_ease-in-out_infinite]"
                   style={{ height: `h{h}%`, animationDelay: `h{i * 0.3}s` }} />
              ))}
            </div>
          </div>
          <div className="border border-white/10 rounded-xl bg-black/50 p-3">
            <small className="font-mono text-[10px] tracking-widest uppercase text-white/30">Anchor accuracy</small>
            <b className="text-white font-bold block">±4 cm <span className="text-white/40 font-normal text-xs">— SKU 7731 sits 3 m from the red anomaly shopper</span></b>
          </div>
          <div className="border border-white/10 rounded-xl bg-black/50 p-3">
            <small className="font-mono text-[10px] tracking-widest uppercase text-white/30">Fusion drift</small>
            <b className="text-white font-bold block">&lt; 0.1%</b>
          </div>
        </div>
      </div>
    </section>
  );
}
